import groupBy from 'lodash-es/groupBy'
import range from 'lodash-es/range'

import {
  getRentalPortalListingsPage,
  performRentalPortalListingsMassAction,
} from '../../../api'
import { EMPTY_LIST, EMPTY_OBJECT } from '../../../constants/common'
import { SUBMENU_PARAM_ARCHIVE_VALUE } from '../../../constants/route'
import i18n from '../../../i18n'
import { isApiSuccessStatus } from '../../../utils/api'
import { getAsyncTaskId } from '../../../utils/async'
import { getApiRequestFilterParams } from '../../../utils/filter'
import {
  getListingUnitLabel,
  LISTING_MASS_ACTION,
} from '../../RentalPortalListing'

import {
  RENTAL_PORTAL_ARCHIVE_OBJECT_TYPE,
  RENTAL_PORTAL_ARCHIVE_SERVED_LISTINGS_REQUEST,
} from './RentalPortalArchive.constants'
import {
  getMockArchivedViewingItems,
  performMockArchivedViewingsRestore,
} from './RentalPortalArchive.mocks'
import { getIsRentalPortalArchiveObjectTypeKnown } from './RentalPortalArchive.utils'

function toArchiveItemFromListing(listing) {
  return {
    archived_at: listing.updated,
    id: `${RENTAL_PORTAL_ARCHIVE_OBJECT_TYPE.listing}-${listing.id}`,
    object_id: listing.id,
    object_name: getListingUnitLabel({ listing, t: i18n.t }),
    object_type: RENTAL_PORTAL_ARCHIVE_OBJECT_TYPE.listing,
    permissions: { can_restore: listing.permissions.can_restore },
    responsible_user: listing.responsible_manager,
  }
}

function getServedArchivedListingsPage(page) {
  return getRentalPortalListingsPage({
    ...RENTAL_PORTAL_ARCHIVE_SERVED_LISTINGS_REQUEST,
    page,
  }).then(({ data }) => data)
}

// TODO: the archive has no endpoint of its own, so it reads every page of the
// archived listings and pages them together with the viewings
function getServedArchivedListings() {
  return getServedArchivedListingsPage(1)
    .then(firstPage =>
      Promise.all(
        range(2, Math.max(firstPage.pagination.page_count, 1) + 1).map(
          getServedArchivedListingsPage
        )
      ).then(restPages => [firstPage, ...restPages])
    )
    .then(pages => pages.flatMap(page => page.items))
}

function getArchivedListingItems() {
  return getIsRentalPortalArchiveObjectTypeKnown(
    RENTAL_PORTAL_ARCHIVE_OBJECT_TYPE.listing
  )
    ? getServedArchivedListings().then(listings =>
        listings.map(toArchiveItemFromListing)
      )
    : Promise.resolve(EMPTY_LIST)
}

function toServedArchivedListingsRestoreResult({ data }) {
  const items = data.items ?? EMPTY_LIST

  return {
    data: {
      items,
      success_ids: items
        .filter(resultItem => isApiSuccessStatus(resultItem.status_code))
        .map(resultItem => resultItem.id),
      task_id: getAsyncTaskId(data),
    },
  }
}

function restoreArchivedListings(ids) {
  const { filters } = getApiRequestFilterParams({
    excludeIds: EMPTY_LIST,
    ids,
    selectAllPages: false,
    sub: SUBMENU_PARAM_ARCHIVE_VALUE,
  })

  return performRentalPortalListingsMassAction({
    action: LISTING_MASS_ACTION.restore,
    all: false,
    filters,
    payload: EMPTY_OBJECT,
  }).then(toServedArchivedListingsRestoreResult)
}

function restoreArchivedObjects({ ids, objectType }) {
  switch (objectType) {
    case RENTAL_PORTAL_ARCHIVE_OBJECT_TYPE.listing:
      return restoreArchivedListings(ids)
    case RENTAL_PORTAL_ARCHIVE_OBJECT_TYPE.viewing:
      return performMockArchivedViewingsRestore(ids)
    default:
      return Promise.resolve({
        data: { items: EMPTY_LIST, success_ids: EMPTY_LIST },
      })
  }
}

function toRentalPortalArchiveMassResultData({ data, objectType }) {
  return {
    items: data.items.map(resultItem => ({
      ...resultItem,
      id: `${objectType}-${resultItem.id}`,
    })),
    success_ids: data.success_ids.map(id => `${objectType}-${id}`),
    task_id: getAsyncTaskId(data),
  }
}

export function getRentalPortalArchiveItems() {
  return getArchivedListingItems().then(listingItems =>
    [...listingItems, ...getMockArchivedViewingItems()].sort((left, right) =>
      String(right.archived_at).localeCompare(String(left.archived_at))
    )
  )
}

export function performRentalPortalArchiveRestore(item) {
  return restoreArchivedObjects({
    ids: [item.object_id],
    objectType: item.object_type,
  })
}

export function performRentalPortalArchiveMassRestore(items) {
  return Promise.all(
    Object.entries(groupBy(items, item => item.object_type)).map(
      ([objectType, typeItems]) =>
        restoreArchivedObjects({
          ids: typeItems.map(item => item.object_id),
          objectType,
        }).then(({ data }) =>
          toRentalPortalArchiveMassResultData({ data, objectType })
        )
    )
  ).then(results => ({
    data: {
      items: results.flatMap(result => result.items),
      success_ids: results.flatMap(result => result.success_ids),
      task_id: results.map(getAsyncTaskId).find(Boolean),
    },
  }))
}
